/* SATRI & CO. — small progressive enhancements on top of Dawn 16.
   Nothing here replaces Dawn's product form, variant, cart, facet or search engines. */
(() => {
  const desktop = window.matchMedia('(min-width: 1000px)');

  /* Tabs for the SATRI product rail (Shop the season / More to explore) */
  if (!customElements.get('satri-tabs')) {
    customElements.define('satri-tabs', class SatriTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        if (!this.tabs.length) return;
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            const map = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
            if (!(e.key in map)) return;
            e.preventDefault();
            let n = map[e.key] === 'first' ? 0 : map[e.key] === 'last' ? this.tabs.length - 1 : (i + map[e.key] + this.tabs.length) % this.tabs.length;
            this.select(n, true);
          });
        });
      }
      select(index, focus) {
        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        this.querySelectorAll('[data-satri-tab-link]').forEach((a) => { a.hidden = Number(a.dataset.satriTabLink) !== index; });
        if (focus) this.tabs[index].focus();
      }
    });
  }

  /* Sticky Add to Bag: mirrors Dawn's real submit button and clicks it — no parallel cart logic. */
  if (!customElements.get('satri-sticky-atb')) {
    customElements.define('satri-sticky-atb', class SatriStickyAtb extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.button = this.querySelector('button');
        this.detail = this.querySelector('[data-satri-sticky-detail]');
        this.label = this.querySelector('[data-satri-sticky-label]');
        this.onClick = () => {
          const main = this.mainButton();
          if (!main || main.disabled) return;
          main.click();
        };
        this.button.addEventListener('click', this.onClick);
        this.bind();
        this.readSelected();
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, (e) => {
            if (!e || !e.data || e.data.sectionId !== this.sectionId) return;
            this.setDetail(e.data.variant);
            requestAnimationFrame(() => this.bind());
          });
        }
      }
      disconnectedCallback() {
        if (this.io) this.io.disconnect();
        if (this.mo) this.mo.disconnect();
        if (this.onScrollFallback) {
          window.removeEventListener('scroll', this.onScrollFallback);
          window.removeEventListener('resize', this.onScrollFallback);
        }
        if (this.unsub) this.unsub();
      }
      mainButton() { return document.querySelector(`#product-form-${this.sectionId} [type="submit"]`); }
      bind() {
        const main = this.mainButton();
        if (!main || main === this.bound) { this.sync(); return; }
        this.bound = main;
        if (this.io) this.io.disconnect();
        if (this.mo) this.mo.disconnect();
        this.io = new IntersectionObserver(([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.classList.toggle('is-visible', past);
          this.setAttribute('aria-hidden', past ? 'false' : 'true');
          this.button.tabIndex = past ? 0 : -1;
        });
        this.io.observe(main);
        this.onScrollFallback = () => {
          if (this._satriRaf) return;
          this._satriRaf = requestAnimationFrame(() => {
            this._satriRaf = null;
            const rect = main.getBoundingClientRect();
            const past = rect.bottom < 0;
            this.classList.toggle('is-visible', past);
            this.setAttribute('aria-hidden', past ? 'false' : 'true');
            this.button.tabIndex = past ? 0 : -1;
          });
        };
        window.addEventListener('scroll', this.onScrollFallback, { passive: true });
        window.addEventListener('resize', this.onScrollFallback);
        this.onScrollFallback();
        this.mo = new MutationObserver(() => this.sync());
        this.mo.observe(main, { attributes: true, childList: true, subtree: true, characterData: true });
        this.sync();
      }
      sync() {
        const main = this.bound;
        if (!main) return;
        this.button.disabled = main.disabled;
        const text = main.querySelector('span');
        if (text && this.label) this.label.textContent = text.textContent.trim();
      }
      readSelected() {
        const json = document.querySelector(`#variant-selects-${this.sectionId} [data-selected-variant]`);
        if (!json) return;
        try { this.setDetail(JSON.parse(json.textContent)); } catch (e) {}
      }
      setDetail(variant) {
        if (!this.detail) return;
        this.detail.textContent = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';
      }
    });
  }

  /* SATRI inline quick-size picker used by the PDP “Style it with” section. */
  if (!customElements.get('satri-quick-size')) {
    customElements.define('satri-quick-size', class SatriQuickSize extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[name="id"]');
        this.submit = this.querySelector('[type="submit"]');
        this.querySelectorAll('[data-variant-id]').forEach((button) => {
          button.addEventListener('click', () => {
            this.querySelectorAll('[data-variant-id]').forEach((b) => {
              b.classList.toggle('is-active', b === button);
              b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
            });
            if (this.input) this.input.value = button.dataset.variantId;
            if (this.submit) {
              this.submit.disabled = false;
              this.submit.removeAttribute('aria-disabled');
            }
          });
        });
      }
    });
  }

  /* Desktop mega menu: open on hover for fine pointers (click + keyboard remain Dawn's). */
  const initHoverMenus = () => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.querySelectorAll('.section-header header-menu details.mega-menu').forEach((details) => {
      if (details.dataset.satriHover) return;
      details.dataset.satriHover = 'true';
      let timer;
      const li = details.closest('li');
      li.addEventListener('mouseenter', () => {
        if (!desktop.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          document.querySelectorAll('.section-header details.mega-menu[open]').forEach((d) => { if (d !== details) d.querySelector('summary').click(); });
          if (!details.open) details.querySelector('summary').click();
        }, 90);
      });
      li.addEventListener('mouseleave', () => clearTimeout(timer));
    });
    const header = document.querySelector('.section-header');
    if (header && !header.dataset.satriHover) {
      header.dataset.satriHover = 'true';
      header.addEventListener('mouseleave', () => {
        if (!desktop.matches) return;
        header.querySelectorAll('details.mega-menu[open]').forEach((d) => d.querySelector('summary').click());
      });
    }
  };

  /* Footer menu columns collapse into disclosures below 1000px. */
  const initFooter = () => {
    document.querySelectorAll('.footer-block--menu').forEach((block, i) => {
      const heading = block.querySelector('.footer-block__heading');
      const list = block.querySelector('.footer-block__details-content');
      if (!heading || !list || block.dataset.satriFooter) return;
      block.dataset.satriFooter = 'true';
      block.classList.add('satri-footer-collapsible');
      list.id = list.id || `SatriFooterMenu-${i}`;
      heading.setAttribute('role', 'button');
      heading.setAttribute('aria-controls', list.id);
      const sync = () => {
        const collapsible = !desktop.matches;
        heading.tabIndex = collapsible ? 0 : -1;
        if (collapsible) heading.setAttribute('aria-expanded', block.classList.contains('is-open') ? 'true' : 'false');
        else heading.removeAttribute('aria-expanded');
      };
      const toggle = () => { if (desktop.matches) return; block.classList.toggle('is-open'); sync(); };
      heading.addEventListener('click', toggle);
      heading.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
      desktop.addEventListener('change', sync);
      sync();
    });
  };

  const init = () => { initHoverMenus(); initFooter(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* SATRI — variant thumbnails on product cards (snippet satri-card-colours) */
(() => {
  if (customElements.get('satri-card-thumbs')) return;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  customElements.define('satri-card-thumbs', class SatriCardThumbs extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('.card-wrapper');
      if (!this.card) return;
      this.img = this.card.querySelector('.card__media .media img');
      if (!this.img) return;
      this.thumbs = Array.from(this.querySelectorAll('.satri-card__thumb'));
      this.active = this.thumbs.find((t) => t.classList.contains('is-active')) || null;
      this.base = { src: this.img.getAttribute('src'), srcset: this.img.getAttribute('srcset') };
      this.thumbs.forEach((t) => {
        t.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.pick(t); });
        t.addEventListener('mouseenter', () => { if (fine.matches) this.show(t); });
      });
      this.addEventListener('mouseleave', () => { if (fine.matches) this.show(this.active); });
    }
    show(t) {
      this.card.classList.add('satri-thumb-picked');
      if (!t) {
        if (this.base.srcset) this.img.setAttribute('srcset', this.base.srcset);
        if (this.base.src) this.img.setAttribute('src', this.base.src);
        return;
      }
      if (t.dataset.srcset) this.img.setAttribute('srcset', t.dataset.srcset);
      if (t.dataset.src) this.img.setAttribute('src', t.dataset.src);
    }
    pick(t) {
      this.active = t;
      this.thumbs.forEach((b) => {
        const on = b === t;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.show(t);
      const url = t.dataset.url;
      if (url) this.card.querySelectorAll('.card__heading a, a.full-unstyled-link').forEach((a) => a.setAttribute('href', url));
    }
  });
})();
